import React, { useState, useEffect, useContext } from "react";
import { AdminSiteContext } from "../contexts/AdminSiteContext";
import { fetchAll } from "../utils/fetchResources";
import { useParams, Prompt } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { onReadyCKE, CKEconfig } from "../config/CKE";
import SetFile from "../components/SetFile";
import {
	Typography,
	Switch,
	FormGroup,
	FormControl,
	InputLabel,
	Input,
	FormControlLabel,
	MenuItem,
	Select,
	Grid,
	Button,
	TextField,
	Hidden,
	Card,
	CardContent,
	useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";

const fullName = (person) => {
	let name = "";
	if (person.firstName) {
		name += person.firstName;
	}
	if (person.middleName) {
		name += " " + person.middleName;
	}
	if (person.lastName) {
		name += " " + person.lastName;
	}
	if (person.suffix) {
		name += " " + person.suffix;
	}
	if (person.title) {
		name += ", " + person.title;
	}
	return name;
};

const useStyles = makeStyles((theme) => ({
	form: {
		width: "100%", // Fix IE 11 issue.
	},
	submit: {
		margin: theme.spacing(2, 0, 1),
	},
	chips: {
		display: "flex",
		flexWrap: "wrap",
	},
	chip: {
		margin: 2,
	},
}));

export default function PeopleEdit(props) {
	const { adminSelectedSite } = useContext(AdminSiteContext);
	const theme = useTheme();
	const classes = useStyles();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [person, setPerson] = useState({
		relatedPeople: [],
		relatedEntries: [],
		published: false,
	});
	const [existingPeople, setExistingPeople] = useState();
	const [existingEntries, setExistingEntries] = useState();
	const [filteredEntries, setFilteredEntries] = useState();
	const [units, setUnits] = useState([]);
	const [sites, setSites] = useState();
	const [publicLink, setPublicLink] = useState();
	const [unsaved, setUnsaved] = useState(false);
	const { enqueueSnackbar } = useSnackbar();

	useEffect(() => {
		const fetchPerson = async (id) => {
			try {
				let res = await fetch(`${process.env.REACT_APP_API_URL}/people/${id}`, {
					credentials: "include",
				});
				res = await res.json();
				if (res.success) {
					setPerson(res.data);
				} else {
					enqueueSnackbar(res.error, { variant: "error" });
				}
			} catch (e) {
				console.log(e);
			}
		};

		(async () => {
			await fetchPerson(id);
			await fetchAll("people", setExistingPeople, "lastName");
			await fetchAll("entries", setExistingEntries, "title");
			await fetchAll("sites", setSites, "name");
			setLoading(false);
		})();
	}, [id, enqueueSnackbar]);

	useEffect(async () => {
		if (existingEntries && adminSelectedSite && adminSelectedSite._id) {
			const filtered = existingEntries.filter(
				(entry) => entry.unit.site._id === adminSelectedSite._id
			);

			setFilteredEntries(filtered);
		} else {
			setFilteredEntries(existingEntries);
		}
	}, [existingEntries, adminSelectedSite]);

	const onChange = (e) => {
		setPerson({ ...person, [e.target.name]: e.target.value });
		setUnsaved(true);
	};

	const onChangeBio = (e, editor) => {
		const data = editor.getData();
		setPerson({ ...person, bio: data });
		setUnsaved(true);
	};

	const onChangeFileCitation = (e, editor) => {
		const data = editor.getData();
		setPerson({ ...person, fileCitation: data });
		setUnsaved(true);
	};

	const onChangePublished = (event) => {
		setPerson({ ...person, [event.target.name]: event.target.checked });
		setUnsaved(true);
	};

	const onSubmit = (e) => {
		e.preventDefault();

		//Remove any keys with an empty str as the value
		//for (const key of Object.keys(person)) {
		//	if (person[key] === "") {
		//		delete person[key];
		//	}
		//}

		fetch(`${process.env.REACT_APP_API_URL}/people/${id}`, {
			method: "PUT",
			credentials: "include",
			body: JSON.stringify(person),
			headers: { "Content-Type": "application/json" },
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.success) {
					setUnsaved(false);
					enqueueSnackbar("Person updated successfully!", {
						variant: "success",
					});
				} else {
					console.log(data);
					const errorArray = data.error.split(",");
					errorArray.forEach((error) => {
						enqueueSnackbar(`${error}`, { variant: "error" });
					});
				}
			});
	};

	if (loading || !filteredEntries) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<Prompt
				when={unsaved}
				message="You have unsaved changes. Are you sure you want to leave?"
			/>
			<Typography component="h1" variant="h4">
				{`Edit Person: ${fullName(person)}`}
			</Typography>
			<Grid container spacing={1}>
				<Grid item xs={12} sm={12} md={9}>
					<Grid container spacing={1}>
						<Grid item xs={12} sm={12} md={4}>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								id="firstName"
								label="First Name"
								required
								name="firstName"
								size="small"
								autoFocus
								onChange={onChange}
								value={(person && person.firstName) ?? ""}
								shrink="true"
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={4}>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								id="middleName"
								label="Middle Name"
								name="middleName"
								size="small"
								onChange={onChange}
								value={(person && person.middleName) ?? ""}
								shrink="true"
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={4}>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								id="lastName"
								label="Last Name"
								name="lastName"
								size="small"
								onChange={onChange}
								value={(person && person.lastName) ?? ""}
								shrink="true"
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={6}>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								id="suffix"
								label="Suffix"
								name="suffix"
								size="small"
								onChange={onChange}
								value={(person && person.suffix) ?? ""}
								shrink="true"
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={6}>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								id="title"
								label="Title"
								name="title"
								size="small"
								onChange={onChange}
								value={(person && person.title) ?? ""}
								shrink="true"
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
					</Grid>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={12} md={6}>
							<FormControl
								fullWidth
								className={classes.formControl}
								size="medium"
								margin="normal"
							>
								<InputLabel id="relatedPeopleLabel">Related People</InputLabel>
								<Select
									inputProps={{ name: "relatedPeople" }}
									labelId="relatedPeople"
									id="relatedPeople"
									multiple
									value={person.relatedPeople ?? []}
									onChange={onChange}
									input={<Input />}
									renderValue={() => "View Selected"}
									disabled={
										existingPeople.filter(
											(eachPerson) => fullName(person) !== fullName(eachPerson)
										).length === 0
									}
								>
									{existingPeople &&
										existingPeople
											.filter(
												(eachPerson) => fullName(eachPerson) != fullName(person)
											)
											.map((person) => {
												let name = "";
												if (person.firstName) {
													name += person.firstName;
												}
												if (person.middleName) {
													name += " " + person.middleName;
												}
												if (person.lastName) {
													name += " " + person.lastName;
												}
												if (person.suffix) {
													name += " " + person.suffix;
												}
												if (person.title) {
													name += ", " + person.title;
												}
												return (
													<MenuItem key={person._id} value={person._id}>
														{name}
													</MenuItem>
												);
											})}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={12} md={6}>
							<FormControl
								fullWidth
								className={classes.formControl}
								size="medium"
								margin="normal"
							>
								<InputLabel id="relatedEntriesLabel">
									Related Entries
								</InputLabel>
								<Select
									inputProps={{ name: "relatedEntries" }}
									labelId="relatedEntries"
									id="relatedEntries"
									multiple
									value={person.relatedEntries}
									onChange={onChange}
									disabled={filteredEntries.length === 0}
									renderValue={() => "View Selected"}
								>
									{existingEntries &&
										filteredEntries &&
										sites.map((site) =>
											filteredEntries
												.filter((entry) => entry.unit.site._id === site._id)
												.map((entry) => (
													<MenuItem key={entry._id} value={entry._id}>
														<Typography
															style={{ fontWeight: "bold" }}
														>{`${site.name}: `}</Typography>
														<Typography
															style={{ marginLeft: ".25rem" }}
														>{`${entry.title}`}</Typography>
													</MenuItem>
												))
										)}
								</Select>
							</FormControl>
						</Grid>
					</Grid>
					<Grid container spacing={2}>
						{/*TODO: Add options for approximate dates, like only knowing the month or year, or even approximate year*/}
						<Grid item xs={12} sm={12} md={6}>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								name="dob"
								label="Date of Birth"
								type="string"
								size="small"
								onChange={onChange}
								value={(person && person.dob) ?? ""}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={6}>
							<TextField
								variant="outlined"
								margin="normal"
								fullWidth
								name="dod"
								label="Date of Death"
								type="string"
								size="small"
								onChange={onChange}
								value={(person && person.dod) ?? ""}
							/>
						</Grid>
					</Grid>
					<Typography style={{ marginTop: "1.5rem", fontWeight: "bold" }}>
						Biography:
					</Typography>
					<CKEditor
						onReady={(editor) => {
							onReadyCKE(editor);
						}}
						data={person.bio || ""}
						editor={Editor}
						onChange={onChangeBio}
						config={CKEconfig("Biography...")}
					/>
					<Typography style={{ marginTop: "1.5rem", fontWeight: "bold" }}>
						File Citation:
					</Typography>
					<CKEditor
						onReady={(editor) => {
							onReadyCKE(editor);
						}}
						data={person.fileCitation || ""}
						editor={Editor}
						onChange={onChangeFileCitation}
						config={CKEconfig("File citation...")}
					/>
					<Button
						type="submit"
						onClick={onSubmit}
						fullWidth
						variant="contained"
						color="primary"
						className={classes.submit}
					>
						Save
					</Button>
				</Grid>
				<Grid item xs={12} sm={12} md={3}>
					<Card>
						<CardContent>
							<Hidden smDown>
								<Button
									type="submit"
									onClick={onSubmit}
									fullWidth
									variant="contained"
									color="primary"
									className={classes.submit}
								>
									Save
								</Button>
							</Hidden>
							<FormGroup row>
								<FormControlLabel
									control={
										<Switch
											color="primary"
											checked={person.published}
											onChange={onChangePublished}
											name="published"
										/>
									}
									label={
										person.published ? (
											<Typography color="primary">Published</Typography>
										) : (
											<Typography color="secondary">Draft</Typography>
										)
									}
								/>
							</FormGroup>
							<SetFile
								fileTitle="Featured Image"
								resource={person}
								setResource={setPerson}
								imageOnly={true}
								fileLabel="featuredImage"
								fileLabelFileName="featuredImageFile"
							/>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</>
	);
}
